import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { List as IList, Map as IMap } from 'immutable';
import {
    Grid, Box, Tab, useMediaQuery, useTheme, Typography
} from '@mui/material';
import { injectIntl, intlShape } from 'react-intl';
import Markdown from 'markdown-to-jsx';
import DOMPurify from 'dompurify';

import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';
import { useFlow } from '../routing/FlowProvider';
import {
    ToggleDigitalIcon, TogglePrintIcon,
    TogglePhysicalIcon
} from '../primitive/icons/pdpIcons';
import { trackButtonClick } from '../segment/segmentModule';
import {
    getAllCategories,
    getDigitalCategories,
    getPhysicalCategories,
    hasDigitalCategories,
    getDigitalPrimaryFaceplate,
    getPhysicalPrimaryFaceplate,
    getDigitalFaceplateGroups,
    getPhysicalFaceplateGroups, hasPhysicalCategories
} from '../landingPage/landingPageSelectors';
import {
    getBrandName,
    getBannerData,
    getBrandElectronicCurrencyList,
    getBrandPlasticCurrencyList,
    getEgcRangedCatalog,
    getPlasticRangedCatalog,
    getBrandSupportedCardTypes
} from '../brand/brandSelectors';
import { getBannerDisplayStatus, getPromoBanners } from '../promo/promoSelectors';
import PromoBanner from '../promo/PromoBanner';
import homePageV2Messages from './assets/messages/homePageMessages';
import { E_CARD_TYPE, PHYSICAL_CARD_TYPE } from '../productCartHandler/productCartModule';
import { getLoaderStatus } from '../loader/loaderSelector';
import SkeletonLoader from '../loader/SkeletonLoader';
import { getIntlCurrencyCode } from '../intl/intlSelectors';
import { getPreferredCardType, getIsSmartFlow } from '../routing/flowSelectors';
import { CARD_TYPES, ITEM_CARD_TYPE } from '../constants/constants';
import {
    selectedCardType
} from '../landingPage/landingPageModule';
import { getEnv, getPreferredCurrencyCode } from '../app/bootstrap';
import { getCurrencyDefault } from '../intl/intlSagas';
import { getCartCurrencyCode } from '../productCartHandler/productCartSelectors';
import Loader from '../loader/loader';
import giftCardDetailsPageMessagesV2 from '../giftCardDetailsPage_v2/messages/giftCardDetailsPageMessages';
import LandingPageFaceplateContainer from './LandingPageFaceplateContainer';
import LandingPageFeatureStripe from './LandingPageFeatureStripe';
import { HOME_STEP } from '../routing/flowConstants';
import useLandingPageLoader from './useLandingPageLoader';

const LandingPage = (props) => {
    const {
        intl, stepName, productDetails, brandName, digitalCategories, physicalCategories,
        supportedElectronicCurrencyList, supportedPlasticCurrencyList, isDigitalCategories, isPhysicalCategories,
        egcCatalogRange, plasticCatalogRange, digitalPrimaryFaceplate,
        isLoading, physicalPrimaryFacePlate, supportedCardTypes, banners,
        isPreferredCardTypePlastic, isSmartFlow, saveSelectedCardType, selectedCurrency, cartCurrencyCode, trackSegmentButtonClick,
        digitalFaceplateGroups, physicalFaceplateGroups
    } = props;
    const { goToNextStep } = useFlow();
    const handleSubmit = isPhysicalCardType => goToNextStep(stepName, {
        overwritingHistoryParams: {
            state: { isPhysicalCardType }
        }
    });

    const theme = useTheme();
    const isMobileView = useMediaQuery(theme.breakpoints.down('md'));

    
    
    
    const hasPromoBanner = banners !== null;
    
    
    const supportsDigital = supportedCardTypes.get('supportsDigitalCards');
    const supportsPhysical = supportedCardTypes.get('supportsPlasticCards');

    
    const getInitialTab = () => {
        if (isDigitalCategories && supportsDigital) return CARD_TYPES.Digital;
        if (isPhysicalCategories && supportsPhysical) return CARD_TYPES.Physical;
        return CARD_TYPES.Digital;
    };

    
    const [activeTab, setActiveTab] = useState(getInitialTab);
    const hasInitializedTab = useRef(false);

    
    useEffect(() => {
        if (hasInitializedTab.current) return;
        if (supportsDigital === undefined && supportsPhysical === undefined) return;

        const correctTab = getInitialTab();
        setActiveTab(correctTab);
        hasInitializedTab.current = true;
    }, [supportsDigital, supportsPhysical, isDigitalCategories, isPhysicalCategories]);

    const onTabChange = (_e, value) => {
        if (value !== activeTab) {
            const tabLabels = {
                [CARD_TYPES.Digital]: isMobileView
                    ? intl.formatMessage(giftCardDetailsPageMessagesV2.digitalGiftCardLabelMobile)
                    : intl.formatMessage(giftCardDetailsPageMessagesV2.digitalGiftCardLabel),
                [CARD_TYPES.Physical]: isMobileView
                    ? intl.formatMessage(giftCardDetailsPageMessagesV2.physicalGiftCardLabelMobile)
                    : intl.formatMessage(giftCardDetailsPageMessagesV2.physicalGiftCardLabel),
                [CARD_TYPES.PrintAtHome]: intl.formatMessage(giftCardDetailsPageMessagesV2.printAtHomeLabel)
            };
            trackSegmentButtonClick(
                tabLabels[value],
                HOME_STEP,
                'Homepage Tab click'
            );
        }
        setActiveTab(value);
    };

    
    const preferredCurrencyCode = getPreferredCurrencyCode();
    const defaultElectronicCurrency = getCurrencyDefault(
        supportedElectronicCurrencyList, preferredCurrencyCode, false, supportedPlasticCurrencyList
    );
    const defaultPlasticCurrency = getCurrencyDefault(
        supportedElectronicCurrencyList, preferredCurrencyCode, true, supportedPlasticCurrencyList
    );

    
    const getInitialDigitalCurrency = () => {
        const currencyExistsInList = supportedElectronicCurrencyList.includes(selectedCurrency);
        const hidePicker = cartCurrencyCode && !currencyExistsInList;
        if (!currencyExistsInList && !hidePicker) {
            return defaultElectronicCurrency;
        }
        return selectedCurrency;
    };

    
    const getInitialPhysicalCurrency = () => {
        const currencyExistsInList = supportedPlasticCurrencyList.includes(selectedCurrency);
        const hidePicker = cartCurrencyCode && !currencyExistsInList;
        if (!currencyExistsInList && !hidePicker) {
            return defaultPlasticCurrency;
        }
        return selectedCurrency;
    };


    
    const [tempDigitalCurrency, setTempDigitalCurrency] = useState(getInitialDigitalCurrency);
    const [tempPhysicalCurrency, setTempPhysicalCurrency] = useState(getInitialPhysicalCurrency);


    const digitalCarouselContainerProps = {
        ...props,
        handleSubmit,
        productDetails,
        categories: digitalCategories,
        giftCardMainTitle: intl.formatMessage(homePageV2Messages.digitalGiftCardTitle),
        submitButtonName: intl.formatMessage(homePageV2Messages.digitalGiftCardCTAButtonText),
        supportedCurrencyList: supportedElectronicCurrencyList,
        catalogRange: egcCatalogRange,
        giftCardDeliveryType: E_CARD_TYPE,
        cardType: CARD_TYPES.Digital,
        digitalPrimaryFaceplate,
        isLoading,
        tempCurrency: tempDigitalCurrency,
        setTempCurrency: setTempDigitalCurrency
    };

    const printAtHomeCarouselContainerProps = {
        ...props,
        handleSubmit,
        productDetails,
        categories: digitalCategories,
        giftCardMainTitle: intl.formatMessage(homePageV2Messages.digitalGiftCardTitle),
        submitButtonName: intl.formatMessage(homePageV2Messages.digitalGiftCardCTAButtonText),
        supportedCurrencyList: supportedElectronicCurrencyList,
        catalogRange: egcCatalogRange,
        giftCardDeliveryType: E_CARD_TYPE,
        digitalPrimaryFaceplate,
        isLoading,
        cardType: CARD_TYPES.PrintAtHome,
        tempCurrency: tempDigitalCurrency,
        setTempCurrency: setTempDigitalCurrency
    };

    const physicalCarouselContainerProps = {
        ...props,
        handleSubmit,
        productDetails,
        categories: physicalCategories,
        giftCardMainTitle: intl.formatMessage(homePageV2Messages.physicalGiftCardTitle),
        submitButtonName: intl.formatMessage(homePageV2Messages.physicalGiftCardCTAButtonText),
        supportedCurrencyList: supportedPlasticCurrencyList,
        catalogRange: plasticCatalogRange,
        giftCardDeliveryType: PHYSICAL_CARD_TYPE,
        cardType: CARD_TYPES.Physical,
        physicalPrimaryFacePlate,
        isLoading,
        tempCurrency: tempPhysicalCurrency,
        setTempCurrency: setTempPhysicalCurrency
    };

    const getTotalFaceplatesCount = (faceplateGroups = IList()) => (
        faceplateGroups.reduce(
            (total, group) => {
                const faceplates = group.get('faceplates', IList());
                return total + (IList.isList(faceplates) ? faceplates.size : 0);
            },
            0
        )
    );

    const activeFaceplateCount = (activeTab === CARD_TYPES.Physical)
        ? getTotalFaceplatesCount(physicalFaceplateGroups)
        : getTotalFaceplatesCount(digitalFaceplateGroups);

    const shouldApplyMinHeight = activeFaceplateCount > 1;
    const landingPageWrapperClassName = shouldApplyMinHeight
        ? 'landing-page-wrapper new_landing-page-wrapper'
        : 'landing-page-wrapper new_landing-page-wrapper grid-view-single-faceplate';

    
    
    
    
    
    
    

    useEffect(() => {
        if (isPreferredCardTypePlastic === null) return;

        if (isPreferredCardTypePlastic === false) {
            saveSelectedCardType(CARD_TYPES.Digital);
            handleSubmit(false);
        }
        if (isPreferredCardTypePlastic === true) {
            saveSelectedCardType(CARD_TYPES.Physical);
            handleSubmit(true);
        }
    }, [isPreferredCardTypePlastic]);

    const { showLoader } = useLandingPageLoader({
        isSmartFlow,
        isLoading,
        digitalCategories,
        physicalCategories,
        supportsDigital,
        supportsPhysical
    });

    if (showLoader) {
        return <Loader showTestEnv={getEnv() !== 'PROD'} />;
    }

    return (
        <Box className={landingPageWrapperClassName}>
            <Grid container spacing={2}>
                <div className='landing-page-header-banner'>
                    {hasPromoBanner && (
                        <Grid item className='promo-banner-container'>
                            {}
                            <SkeletonLoader
                                isLoading={isLoading}
                                skeletonProps={{
                                    animation: 'wave',
                                    variant: 'rectangular',
                                    className: 'skeletonclass',
                                    height: 85
                                }}
                            >
                                <PromoBanner />
                            </SkeletonLoader>
                        </Grid>
                    )}
                </div>

                <Grid className='new-landing-page-content-container-grid' item xs={12}>
                    <TabContext name={ITEM_CARD_TYPE} value={activeTab}>
                        {}
                        <Grid container className='new-landing-page-toggle-container'>
                            <Grid item xs={7} className='new-landing-page-title-container'>
                                <SkeletonLoader
                                    isLoading={isLoading}
                                    skeletonProps={{
                                        animation: 'wave',
                                        variant: 'rectangular',
                                        className: 'skeletonclass',
                                        height: 70,
                                        width: '90%',
                                        style: { marginBottom: '16px' }
                                    }}
                                >
                                    <Typography
                                        className='new-landing-page-title'
                                        variant="h3"
                                        component="h1"
                                        sx={{ px: 3, pt: 2 }}
                                    >
                                        <Markdown>
                                            {DOMPurify.sanitize(intl.formatMessage(
                                                homePageV2Messages.homePageV2HeaderTitle,
                                                { brandName }
                                            ))}
                                        </Markdown>
                                    </Typography>
                                </SkeletonLoader>
                            </Grid>

                            {}
                            <Grid item xs={5} className='togglePDP-parent-container'>
                                <Box className='togglePDP-container'>
                                    <SkeletonLoader
                                        isLoading={isLoading}
                                        skeletonProps={{
                                            animation: 'wave',
                                            variant: 'rectangular',
                                            className: 'skeletonclass',
                                            height: 70
                                        }}
                                    >
                                        <TabList
                                            variant="fullWidth"
                                            onChange={onTabChange}
                                            aria-label="Delivery options"
                                        >
                                            {supportsDigital && (
                                                <Tab
                                                    className='toggle-Egift-Tab'
                                                    label={isMobileView
                                                        ? intl.formatMessage(
                                                            giftCardDetailsPageMessagesV2.digitalGiftCardLabelMobile
                                                        )
                                                        : intl.formatMessage(
                                                            giftCardDetailsPageMessagesV2.digitalGiftCardLabel
                                                        )}
                                                    icon={<ToggleDigitalIcon />}
                                                    value={CARD_TYPES.Digital}
                                                    name={CARD_TYPES.Digital}
                                                    role="tab"
                                                />
                                            )}
                                            {supportsPhysical && (
                                                <Tab
                                                    className='toggle-Egift-Tab'
                                                    label={isMobileView
                                                        ? intl.formatMessage(
                                                            giftCardDetailsPageMessagesV2.physicalGiftCardLabelMobile
                                                        )
                                                        : intl.formatMessage(
                                                            giftCardDetailsPageMessagesV2.physicalGiftCardLabel
                                                        )}
                                                    icon={<TogglePhysicalIcon />}
                                                    value={CARD_TYPES.Physical}
                                                    name={CARD_TYPES.Physical}
                                                    role="tab"
                                                />
                                            )}
                                            {supportsDigital && (
                                                <Tab
                                                    className='toggle-Egift-Tab'
                                                    label={intl.formatMessage(
                                                        giftCardDetailsPageMessagesV2.printAtHomeLabel
                                                    )}
                                                    icon={<TogglePrintIcon />}
                                                    value={CARD_TYPES.PrintAtHome}
                                                    name={CARD_TYPES.PrintAtHome}
                                                    role="tab"
                                                />
                                            )}
                                        </TabList>
                                    </SkeletonLoader>
                                </Box>
                            </Grid>
                        </Grid>

                        {isDigitalCategories && supportsDigital && (
                            <TabPanel
                                value={CARD_TYPES.Digital}
                                className='tabContent'
                                sx={shouldApplyMinHeight ? { minHeight: 'calc(100vh - 500px)' } : {}}
                            >
                                <LandingPageFaceplateContainer {...digitalCarouselContainerProps} />
                            </TabPanel>
                        )}

                        {isPhysicalCategories && supportsPhysical && (
                            <TabPanel
                                value={CARD_TYPES.Physical}
                                className='tabContent'
                                sx={shouldApplyMinHeight ? { minHeight: 'calc(100vh - 500px)' } : {}}
                            >
                                <LandingPageFaceplateContainer {...physicalCarouselContainerProps} />
                            </TabPanel>
                        )}

                        {isDigitalCategories && supportsDigital && (
                            <TabPanel
                                value={CARD_TYPES.PrintAtHome}
                                className='tabContent'
                                sx={shouldApplyMinHeight ? { minHeight: 'calc(100vh - 500px)' } : {}}
                            >
                                <LandingPageFaceplateContainer {...printAtHomeCarouselContainerProps} />
                            </TabPanel>
                        )}

                        {!supportsDigital && !supportsPhysical && (
                            <Box className='tabContent' sx={{ padding: 3 }}>
                                <Typography variant="h5" className='no-inventory-message'>
                                    {intl.formatMessage(homePageV2Messages.noInventoryMessage)}
                                </Typography>
                            </Box>
                        )}

                    </TabContext>
                </Grid>

                <Grid item xs={12} className='LandingPageFeatureStripe-container'>
                    <LandingPageFeatureStripe
                        activeTab={activeTab}
                        supportsPhysical={supportsPhysical}
                        supportsDigital={supportsDigital}
                        stepName={stepName}
                        digitalPrimaryFaceplate={digitalPrimaryFaceplate}
                        physicalPrimaryFacePlate={physicalPrimaryFacePlate}
                        digitalCategories={digitalCategories}
                        physicalCategories={physicalCategories}
                        supportedElectronicCurrencyList={supportedElectronicCurrencyList}
                        supportedPlasticCurrencyList={supportedPlasticCurrencyList}
                        cartCurrencyCode={cartCurrencyCode}
                        defaultElectronicCurrency={defaultElectronicCurrency}
                        defaultPlasticCurrency={defaultPlasticCurrency}
                    />
                </Grid>
            </Grid>
        </Box>
    );
};

LandingPage.defaultProps = {
    brandName: '',
    productDetails: null,
    supportedElectronicCurrencyList: IList(),
    supportedPlasticCurrencyList: IList(),
    digitalFaceplateGroups: IList(),
    physicalFaceplateGroups: IList(),
    digitalPrimaryFaceplate: IMap(),
    physicalPrimaryFacePlate: IMap(),
    supportedCardTypes: IMap(),
    checkoutSubHeaderText: '',
    banners: null,
    selectedCurrency: '',
    cartCurrencyCode: ''
};

LandingPage.propTypes = {
    intl: intlShape.isRequired,
    stepName: PropTypes.string.isRequired,
    categories: PropTypes.instanceOf(IList).isRequired,
    bannerData: PropTypes.instanceOf(IList).isRequired,
    displayBanners: PropTypes.bool.isRequired,
    productDetails: PropTypes.object,
    brandName: PropTypes.string,
    digitalCategories: PropTypes.instanceOf(IList).isRequired,
    physicalCategories: PropTypes.instanceOf(IList).isRequired,
    supportedElectronicCurrencyList: PropTypes.instanceOf(IList),
    supportedPlasticCurrencyList: PropTypes.instanceOf(IList),
    digitalFaceplateGroups: PropTypes.instanceOf(IList),
    physicalFaceplateGroups: PropTypes.instanceOf(IList),
    isDigitalCategories: PropTypes.bool.isRequired,
    isPhysicalCategories: PropTypes.bool.isRequired,
    egcCatalogRange: PropTypes.object.isRequired,
    plasticCatalogRange: PropTypes.object.isRequired,
    digitalPrimaryFaceplate: PropTypes.instanceOf(IMap),
    isLoading: PropTypes.bool.isRequired,
    physicalPrimaryFacePlate: PropTypes.instanceOf(IMap),
    supportedCardTypes: PropTypes.instanceOf(IMap),
    checkoutSubHeaderText: PropTypes.string,
    banners: PropTypes.object,
    isPreferredCardTypePlastic: PropTypes.bool.isRequired,
    isSmartFlow: PropTypes.bool.isRequired,
    saveSelectedCardType: PropTypes.func.isRequired,
    trackSegmentButtonClick: PropTypes.func.isRequired,
    selectedCurrency: PropTypes.string,
    cartCurrencyCode: PropTypes.string
};

const mapStateToProps = state => ({
    categories: getAllCategories(state),
    digitalCategories: getDigitalCategories(state),
    physicalCategories: getPhysicalCategories(state),
    bannerData: getBannerData(state),
    displayBanners: getBannerDisplayStatus(state),
    brandName: getBrandName(state),
    supportedElectronicCurrencyList: getBrandElectronicCurrencyList(state),
    supportedPlasticCurrencyList: getBrandPlasticCurrencyList(state),
    isDigitalCategories: hasDigitalCategories(state),
    isPhysicalCategories: hasPhysicalCategories(state),
    egcCatalogRange: getEgcRangedCatalog(state),
    plasticCatalogRange: getPlasticRangedCatalog(state),
    digitalPrimaryFaceplate: getDigitalPrimaryFaceplate(state),
    isLoading: getLoaderStatus(state),
    physicalPrimaryFacePlate: getPhysicalPrimaryFaceplate(state),
    supportedCardTypes: getBrandSupportedCardTypes(state),
    banners: getPromoBanners(state),
    isPreferredCardTypePlastic: getPreferredCardType(state),
    isSmartFlow: getIsSmartFlow(state),
    selectedCurrency: getIntlCurrencyCode(state),
    cartCurrencyCode: getCartCurrencyCode(state),
    digitalFaceplateGroups: getDigitalFaceplateGroups(state),
    physicalFaceplateGroups: getPhysicalFaceplateGroups(state)
});

const mapDispatchToProps = {
    saveSelectedCardType: selectedCardType,
    trackSegmentButtonClick: trackButtonClick
};

export default connect(mapStateToProps, mapDispatchToProps)(injectIntl(LandingPage));
